/**
 * AD-009 layer 1, for the screens that are more than a column of buttons.
 *
 * The Main Menu fits `menu-model.ts`'s `MenuScreen` exactly — a title and a
 * list of labels — because that is all `PS.EXE` puts on it. FLY SINGLE
 * MISSION, the briefings, the Career hub and the Realism Panel do not: they
 * mix buttons with list boxes, sunken value fields, checkboxes, prose blocks
 * and photographs. So a screen here is a **list of widgets** in logical
 * 320×200 coordinates, still plain data, still free of canvases and Solid.
 *
 * Two consequences worth stating, because they are the whole point of the
 * shape:
 *
 * - the geometry is assertable. A test can say "the mission grid's tenth slot
 *   is at (164,137,130,18)" against the number measured off
 *   `fly-single-mission.png` without rendering anything.
 * - one focus model covers every screen. `interactiveWidgets` is the ordered
 *   list the arrow keys walk and the mouse hit-tests into, derived from the
 *   widgets rather than maintained beside them, so a control can never be
 *   drawn somewhere the keyboard cannot reach.
 */

import type { Rect } from "../render/draw-ops.ts";
import type { MenuAction } from "./menu-model.ts";

/**
 * Which of the original's text treatments a string is inked in. The captures
 * only ever use five, and they differ in two things: the font, and the
 * background the emboss is computed against (`plate-chrome.ts`'s `embossOn`).
 */
export type TextRole =
  /** FONT1 on a plate face — `FLIGHT ROSTER`, `Enlistment in the …`. */
  | "heading"
  /** FONT3 bold on a plate face — `SELECT HISTORIC MISSION:`. */
  | "prompt"
  /** FONT3 on a plate face — field labels, table cells. */
  | "label"
  /** FONT3 on the panel's flat body — prose paragraphs, sub-captions. */
  | "prose"
  /** FONT3 inside a boxed template slot. */
  | "slot"
  /** FONT1 on an orange plate — the Mission Debriefing's field names. */
  | "orangeHeading"
  /** FONT3 on an orange plate. */
  | "orangeLabel"
  /** TALK.FNT on the panel's flat body — the Mission Debriefing's narrative, and the only place the game uses it. */
  | "talk";

export interface ProseBlock {
  readonly lines: readonly string[];
  readonly x: number;
  readonly y: number;
  readonly role: TextRole;
  /** Rows between successive lines. The captures use 9 for FONT3 prose and 8 for tight tables. */
  readonly pitch: number;
}

export interface ListRow {
  readonly id: string;
  readonly label: string;
  /** Right-aligned second column — the ace roster's kill counts, the roster's dates. */
  readonly trailing?: string;
}

/**
 * `button` covers both the board's raised orange plates and the panels' action
 * row; they differ only in size, which the caller has already decided.
 * `value` is the Flight Assignments selector — an orange plate with a sunken
 * value in it — and is interactive when it has an action, decorative when it
 * does not. `slot` is the original's inline `[boxed]` briefing template value
 * — "clicking on one will bring up a menu with your possible options"
 * (manual, Fly Single Mission) — and follows the same rule: interactive with
 * an `action`, decorative without one, same pixels either way.
 */
export type Widget =
  | { readonly kind: "button"; readonly id: string; readonly rect: Rect; readonly label: string; readonly enabled: boolean; readonly action: MenuAction }
  | { readonly kind: "plate"; readonly rect: Rect }
  /** The debriefing's orange stats face: the same bevel as a button plate, at any size. */
  | { readonly kind: "orangePlate"; readonly rect: Rect }
  /**
   * A sunken well. `face` overrides the black interior — the formation
   * thumbnail is a light-grey plate. `orange` frames it on the burnt-orange
   * ramp, which is what a well sunk into the debriefing's stats plate needs.
   */
  | { readonly kind: "well"; readonly rect: Rect; readonly face?: number; readonly orange?: boolean }
  | { readonly kind: "text"; readonly text: string; readonly x: number; readonly y: number; readonly role: TextRole }
  | { readonly kind: "prose"; readonly block: ProseBlock }
  | {
      readonly kind: "list";
      readonly id: string;
      readonly rect: Rect;
      readonly rows: readonly ListRow[];
      readonly selected: number;
      /** Index of the first visible row, so a long roster scrolls without the model growing a scroll widget. */
      readonly top: number;
      readonly action: MenuAction;
    }
  | {
      readonly kind: "value";
      readonly id: string;
      readonly rect: Rect;
      readonly text: string;
      readonly enabled: boolean;
      readonly action: MenuAction | undefined;
    }
  | {
      readonly kind: "toggle";
      readonly id: string;
      readonly box: Rect;
      readonly plate: Rect;
      readonly label: string;
      readonly checked: boolean;
      readonly enabled: boolean;
      readonly action: MenuAction;
    }
  | { readonly kind: "slot"; readonly rect: Rect; readonly text: string; readonly action?: MenuAction }
  /**
   * A value printed flat inside a black well — the Realism Panel's readouts and
   * Mission Debriefing's counts. The two captures disagree about where the
   * value sits, so the widget carries the alignment rather than assuming one:
   * `realism-panel.png` centres its readouts in their wells, while
   * `mission-debriefing.png` puts every number's first ink column at
   * `rect.x + 5` — measured on both the 66-wide MISSION SCORE well and the
   * 21-wide victory wells, so it is a rule and not a one-digit accident.
   * Centred is the default because it is what every caller but the debriefing
   * wants.
   */
  | { readonly kind: "wellText"; readonly rect: Rect; readonly text: string; readonly align?: "center" | "left" }
  | { readonly kind: "picture"; readonly sheet: string; readonly frame: number; readonly x: number; readonly y: number }
  | { readonly kind: "rules"; readonly x: number; readonly y: number; readonly length: number; readonly toward: -1 | 1 }
  /**
   * The engraved rule under a field name — `SITUATION`, `MISSION PLAN` — two
   * rows, dark over light. `orange` draws it on the burnt-orange ramp instead
   * of the tan one, which is what `mission-debriefing.png` rules `VICTORIES`
   * with.
   */
  | { readonly kind: "underline"; readonly x: number; readonly y: number; readonly w: number; readonly orange?: boolean };

/** How the frame around the widgets is drawn. */
export type ScreenChrome =
  | { readonly kind: "board"; readonly board: BoardChrome; readonly background: string | undefined }
  | { readonly kind: "panel"; readonly title: string; readonly emblem: PanelEmblem; readonly background: string | undefined }
  /**
   * Nothing at all behind the widgets — no board, no panel, no background.
   *
   * For a surface that is *only* a dialog, composited over a picture something
   * else owns: G38's in-flight `ESC` board over the cockpit. Every other screen
   * in the shell fills the frame, which is why this did not exist until a screen
   * needed deliberately not to.
   */
  | { readonly kind: "none" };

export type PanelEmblem = "roundel" | "cross" | "none";

/** Only what the chrome renderer needs; the button rects live in the widget list. */
export interface BoardChrome {
  readonly frame: Rect;
  readonly title: string;
  readonly ridgeInsets: readonly [number, number];
  readonly footerInsets: readonly [number, number];
}

export interface ShellScreen {
  readonly id: string;
  readonly chrome: ScreenChrome;
  readonly widgets: readonly Widget[];
  /** What `Escape` does. Every screen but a surface's root has one. */
  readonly cancel: MenuAction | undefined;
  /**
   * A hole in the 2D frame where a live 3D view is composited — the original's
   * PERSONAL AIRCRAFT turntable, which is the one shell screen that is not
   * drawn entirely out of the palette. The rect is logical (320x200) like every
   * other; `ScreenShell` maps it through the same letterbox transform so the
   * inset lands exactly on the well drawn under it, at any window size.
   */
  readonly viewport?: Rect;
  /**
   * A `MUSIC_PATHS` key this screen wants playing under it, replacing the shell
   * theme for as long as it is up and giving way again when the flow moves on.
   * `undefined` — every screen but one — means the shell theme.
   *
   * The Board of Inquiry is the case this exists for: `PS.EXE` names
   * `board.sng` beside `boi.dat`, and no `.TTM` in the corpus is a hearing
   * scene, so its music has no cutscene to ride in on the way `MEDAL.SNG` and
   * `PROMOTE.SNG` do.
   */
  readonly music?: string;
}

/**
 * A popup over the current screen — the original's own dialog widget
 * (`dialog-who-will-you-fly-for.png`), used for the side picker, the
 * consequences of a sortie, and the eight mission types that refuse. Rows may
 * carry an emblem, which is what makes the side picker the side picker.
 */
export interface DialogRow {
  readonly id: string;
  readonly label: string;
  readonly emblem: PanelEmblem;
  readonly action: MenuAction;
}

export interface ShellDialog {
  readonly title: string;
  readonly body: readonly string[];
  readonly rows: readonly DialogRow[];
  readonly focus: number;
  /**
   * Force the board at least this wide, in logical pixels.
   *
   * `popup-chrome.ts` sizes a board from its title and its labels and states
   * plainly that "the box is ours" — a liberty taken because the shell needs
   * popups the original never had. This is the escape hatch for the opposite
   * case: a dialog the original *does* have, whose width has been measured off a
   * capture. G38's `ESC` board is the only one so far (196 px, which is also
   * `POPUP_MAX_WIDTH`, because that ceiling was derived from the same capture).
   *
   * Optional, and absent leaves every existing dialog sized exactly as before.
   */
  readonly minWidth?: number;
  /**
   * A sunken well drawn under the rows, outside the button column.
   *
   * Exists for the one thing the `ESC` board has that no other popup in the port
   * does: a live `SCORE:` readout, in red on black, below the last button. It is
   * a string rather than a number so the caller owns the wording — the label is
   * the original's and the figure behind it is the port's own invented
   * weighting, and those are two different provenances that should not be
   * decided here.
   */
  readonly footer?: string;
}

export function dialogRow(id: string, label: string, action: MenuAction, emblem: PanelEmblem = "none"): DialogRow {
  return { id, label, emblem, action };
}

/** A one-button "this is not flying yet" popup, in the original's own style. */
export function noticeDialog(title: string, body: readonly string[]): ShellDialog {
  return { title, body, rows: [dialogRow("continue", "CONTINUE", { kind: "close" })], focus: 0 };
}

export function moveDialogFocus(dialog: ShellDialog, delta: number): ShellDialog {
  const count = dialog.rows.length;
  if (count === 0) return dialog;
  return { ...dialog, focus: (((dialog.focus + delta) % count) + count) % count };
}

export type InteractiveWidget =
  | Extract<Widget, { kind: "button" | "list" | "toggle" }>
  | Extract<Widget, { kind: "value" | "slot" }>;

function isInteractive(widget: Widget): widget is InteractiveWidget {
  if (widget.kind === "value" || widget.kind === "slot") return widget.action !== undefined;
  return widget.kind === "button" || widget.kind === "list" || widget.kind === "toggle";
}

/** The controls the arrow keys walk, in the order they are declared. Disabled ones stay in the list so the cursor can rest on them and the player can read why. */
export function interactiveWidgets(screen: ShellScreen): readonly InteractiveWidget[] {
  return screen.widgets.filter(isInteractive);
}

export function widgetRect(widget: InteractiveWidget): Rect {
  return widget.kind === "toggle" ? widget.plate : widget.rect;
}

/** Wraps at both ends, matching the original's arrow-key behaviour on a fixed control list. */
export function moveFocus(screen: ShellScreen, focus: number, delta: number): number {
  const count = interactiveWidgets(screen).length;
  if (count === 0) return 0;
  return (((focus + delta) % count) + count) % count;
}

/** Index of the control under a logical point, or `undefined` for dead space. */
export function focusAt(screen: ShellScreen, x: number, y: number): number | undefined {
  const index = interactiveWidgets(screen).findIndex((widget) => {
    const rect = widgetRect(widget);
    return x >= rect.x && y >= rect.y && x < rect.x + rect.w && y < rect.y + rect.h;
  });
  return index < 0 ? undefined : index;
}
